import { useRecoilState } from 'recoil';
import { QuoteProductItem, SelectedQuoteItem } from '../../interfaces';
import { SelectedSubscriptionPlan } from '../../interfaces/QuoteItem.types';
import { useQuoteProductInformation } from '../../hooks/use-quote-product-information';
import { useAddLineItems } from '../../hooks/use-add-quote-line-items';
import {
  DEFAULT_QUOTE_INFO,
  generateProductInformationPayload,
  generateProductLines,
  generateQuoteLinesPayload,
  QuoteInformationAtom,
  quoteInformationAtom,
  getCMPToken,
} from '../internal';
import { useLocation } from 'react-router-dom';
import { useCallback, useEffect } from 'react';

export const QUOTE_EDITOR_URL = 'my/quotemgmt/quoteitems';

export const useAddToQuote = () => {
  const location = useLocation();
  const { isPending, mutateAsync: getProductInformation } =
    useQuoteProductInformation();
  const { mutateAsync: addLineItems } = useAddLineItems();

  const [quoteInfo, setQuoteInfo] = useRecoilState(quoteInformationAtom);

  const showQuoteButton = Boolean(quoteInfo.quoteNumber);

  const setGlobalQuoteInfo = (quoteInfo: QuoteInformationAtom) => {
    setQuoteInfo(quoteInfo);
  };

  const addTSProducts = async (product: QuoteProductItem) => {
    const productItems = generateProductInformationPayload(
      product,
      quoteInfo.branchNumber
    );

    if (productItems.length === 0) return;

    const skus = await getProductInformation(productItems);
    const newQuoteLines = generateProductLines([product], skus?.validSkus);

    if (newQuoteLines && newQuoteLines.length > 0) {
      const quoteLinesPayload = generateQuoteLinesPayload(
        newQuoteLines,
        'Addtoquote',
        quoteInfo
      );
      await addLineItems(quoteLinesPayload);
    }
  };

  const addCloudProducts = async (subscription: SelectedSubscriptionPlan) => {
    // eslint-disable-next-line no-console
    console.log('subscription', subscription);
    let quoteLinesPayload: any;
    const newPlanOptions: any[] = [];
    const updatedPlanOptions: any[] = [];
    subscription.subscriptionOptions?.forEach((option) => {
      const planOptionsExists: any =
        quoteInfo.quoteLinesItems?.find(
          (line: any) => line?.vpn && line?.vpn === option.vpn
        ) || {};
      if (planOptionsExists && Object.keys(planOptionsExists).length > 0) {
        updatedPlanOptions.push({
          ...planOptionsExists,
          quantity:
            (Number(planOptionsExists?.quantity) || 0) +
            (Number(option.quantity) || 0),
        });
      } else {
        newPlanOptions.push(option);
      }
    });

    if (newPlanOptions?.length > 0) {
      quoteLinesPayload = generateQuoteLinesPayload(
        newPlanOptions,
        'Addtoquote',
        quoteInfo
      );
      // }
    }
    if (updatedPlanOptions?.length > 0) {
      const updatedSubscriptionOptions = updatedPlanOptions.map((option) => {
        return {
          duplicateselected: true,
          lineId: option?.lineNumber,
          markUp: option?.markUp,
          markUpUnit: option?.markupUnit,
          quantity: option?.quantity.toString(),
          sequenceNumber: option?.sequenceNumber,
          skuvpn: option?.pnaResponse?.ingramPartNumber,
          vendorNumber:
            option?.pnaResponse?.productAttributes?.[0]?.vendorNumber,
          vpn: option?.panResponse?.vendorPartNumber,
        };
      });

      if (updatedSubscriptionOptions?.length > 0) {
        quoteLinesPayload = generateQuoteLinesPayload(
          updatedSubscriptionOptions,
          'Updatequantity',
          quoteInfo
        );
      }
    }
    if (quoteLinesPayload) {
      const response = await addLineItems({
        ...quoteLinesPayload,
        cmptoken: getCMPToken(),
      });
      return response;
    }
    return null;
  };

  const addToQuote = async (
    productType: keyof SelectedQuoteItem,
    product?: QuoteProductItem | null,
    subscription?: any
  ) => {
    if (productType === 'product') {
      await addTSProducts(product as QuoteProductItem);
    }
    if (productType === 'subscription') {
      await addCloudProducts(subscription as SelectedSubscriptionPlan);
    }
  };

  const clearQuote = useCallback(
    () => setQuoteInfo(DEFAULT_QUOTE_INFO),
    [setQuoteInfo]
  );

  useEffect(() => {
    if (showQuoteButton) {
      const path = location.pathname.split('/');
      if (!(path.includes('product') || path.includes('quotemgmt'))) {
        clearQuote();
      }
    }
  }, [clearQuote, location, showQuoteButton]);

  return {
    addToQuote,
    clearQuote,
    isLoading: isPending,
    quoteEditorURL: `/${QUOTE_EDITOR_URL}?quoteId=${quoteInfo.quoteNumber}&quoteGuid=${quoteInfo.quoteGuid}&quoteType=QMDraft`,
    quoteInfo,
    setGlobalQuoteInfo,
    showQuoteButton,
  };
};
